import { useCallback } from "react";
import { useFetcher, useMutator } from "~/hooks/useFetcher";
import {
  userPreferencesPath,
  type UserPreferencesResponse,
  type UserPreferencesUpdate,
} from "~/services/preferences";

export interface McpGuidelines {
  /** Whether the user has accepted the MCP guidelines. `null` until the
   *  preference has loaded. Fails closed: a read error resolves to `false`
   *  (not accepted) so a transient failure can never opt a user in. */
  accepted: boolean | null;
  /** True while the acceptance is being persisted. */
  isAccepting: boolean;
  /** Records acceptance, then revalidates so `accepted` flips to true. */
  onAccept: () => void;
}

/**
 * Reads the user's MCP-guidelines acceptance from the conversations preferences
 * endpoint and exposes a one-way accept action. Connectors are gated behind
 * acceptance — third-party MCP servers cannot be enabled until the user agrees
 * to the guidelines, so this fails closed rather than blocking on the UI.
 */
export function useMcpGuidelines(): McpGuidelines {
  const key = userPreferencesPath();
  const { data, error } = useFetcher<UserPreferencesResponse>(key);
  const { trigger, isMutating } = useMutator<UserPreferencesResponse, UserPreferencesUpdate>(key, {
    requestInit: { method: "PUT" },
  });

  // null while loading; fail closed (false) on error so a failed read never
  // grants access; otherwise reflect the persisted timestamp.
  const accepted = data ? data.mcp_guidelines_accepted_at != null : error ? false : null;

  const onAccept = useCallback(() => {
    void trigger({ accept_mcp_guidelines: true });
  }, [trigger]);

  return { accepted, isAccepting: isMutating, onAccept };
}
